import * as React from "react";
import { GetInsertedCharacterCount } from "../Helpers/QuickJournalTypingRateHelpers";

const typingRateWindowInMilliseconds = 4000;
const typingRateWindowInSeconds = typingRateWindowInMilliseconds / 1000;
const typingRateUpdateIntervalInMilliseconds = 800;
const typingRateGreenUpdateIntervalInMilliseconds = 500;
const typingRateOrangeUpdateIntervalInMilliseconds = 100;

export function useQuickJournalTypingCharactersPerSecond(
    value: string
): {
    charactersPerSecond: number;
    recordTypingSample: (insertedCharacters: number) => void;
} {
    const didInitializeRef = React.useRef(false);
    const lastValueRef = React.useRef(value);
    const typingSamplesRef = React.useRef<
        Array<{ time: number; insertedCharacters: number }>
    >([]);
    const [charactersPerSecond, setCharactersPerSecond] = React.useState(0);

    const updateCharactersPerSecond = React.useCallback(() => {
        const now = Date.now();
        const windowStart = now - typingRateWindowInMilliseconds;

        typingSamplesRef.current = typingSamplesRef.current.filter(
            (sample) => sample.time >= windowStart
        );

        const totalInsertedCharacters = typingSamplesRef.current.reduce(
            (sum, sample) => sum + sample.insertedCharacters,
            0
        );
        const nextCharactersPerSecond =
            totalInsertedCharacters / typingRateWindowInSeconds;

        setCharactersPerSecond((currentCharactersPerSecond) =>
            Math.abs(
                currentCharactersPerSecond - nextCharactersPerSecond
            ) < 0.05
                ? currentCharactersPerSecond
                : nextCharactersPerSecond
        );
    }, []);

    React.useEffect(() => {
        if (!didInitializeRef.current) {
            didInitializeRef.current = true;
            lastValueRef.current = value;
            return;
        }

        if (value === lastValueRef.current) {
            return;
        }

        const insertedCharacters = GetInsertedCharacterCount(
            lastValueRef.current,
            value
        );

        lastValueRef.current = value;

        if (insertedCharacters > 0) {
            typingSamplesRef.current.push({
                time: Date.now(),
                insertedCharacters,
            });
        }
    }, [value]);

    React.useEffect(() => {
        const nextTypingRateUpdateIntervalInMilliseconds =
            charactersPerSecond >= 4
                ? typingRateOrangeUpdateIntervalInMilliseconds
                : charactersPerSecond >= 2
                  ? typingRateGreenUpdateIntervalInMilliseconds
                : typingRateUpdateIntervalInMilliseconds;

        const intervalId = window.setInterval(
            updateCharactersPerSecond,
            nextTypingRateUpdateIntervalInMilliseconds
        );

        return () => window.clearInterval(intervalId);
    }, [charactersPerSecond, updateCharactersPerSecond]);

    const recordTypingSample = React.useCallback(
        (insertedCharacters: number) => {
            if (insertedCharacters <= 0) {
                return;
            }

            typingSamplesRef.current.push({
                time: Date.now(),
                insertedCharacters,
            });
        },
        []
    );

    return {
        charactersPerSecond,
        recordTypingSample,
    };
}
